import { c as _c } from "react/compiler-runtime";
import { ChatMessageRole, MIMIR_BASE_AGENT_ID } from 'src/mimir/constants';
import { type ChatMessage } from 'src/mimir/types';
import { chatMessageFactory, getContextFromMessages } from 'src/mimir/utils';
import { type ReactElement, type ReactNode, createContext, useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { useMutationMimirAgentChat } from 'src/mimir/api';
import { useCurrentClient } from './ClientContext';
import { type ClientError } from 'shared/api/fetch';
import { type MimirAgentMessage, type MimirAgentChatRequest } from 'shared/api/mimir';
import { useQueryUserProfile } from 'src/profile/api';
interface MimirContextProps {
  isMimirOpen: boolean;
  toggleMimir: () => void;
  askMimir: (questionRequest: {
    userFacingMessage: string;
    questionPrompt?: string;
    clearHistory?: boolean;
    options?: {
      onError: (error: ClientError, variables: MimirAgentChatRequest, context: unknown) => void;
    };
    customDataContext?: string;
    openIfClosed?: boolean;
  }) => void;
  messages: ChatMessage[];
  clearMessageHistory: () => void;
  isWaitingForResponse: boolean;
  dataContext?: string;
  suggestedQuestions?: string[];
  setDataContext: (dataContext: string | undefined) => void;
  setSuggestedQuestions: (suggestedQuestions: string[] | undefined) => void;
}
const MimirContext = createContext<MimirContextProps | undefined>(undefined);
export const useMimir = () => {
  const mimirContext = useContext(MimirContext);
  if (mimirContext == null) {
    throw new Error("No mimirContext.Provider found when calling mimirContext.");
  }
  return mimirContext;
};
export const MimirProvider = t0 => {
  const $ = _c(21);
  const {
    children
  } = t0;
  const [isMimirOpen, setIsMimirOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [messages, setMessages] = useState(t1);
  const {
    currentUser
  } = useAuth();
  const {
    data: currentUserDetails
  } = useQueryUserProfile(currentUser?.id);
  const {
    currentClient
  } = useCurrentClient();
  const [dataContext, setDataContext] = useState();
  const [suggestedQuestions, setSuggestedQuestions] = useState();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsMimirOpen(_temp);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggleMimir = t2;
  const {
    mutate: submitQuestionToAgent,
    isPending: isSubmittingQuestion
  } = useMutationMimirAgentChat();
  let askMimir;
  let clearMessageHistory;
  if ($[2] !== currentClient || $[3] !== currentUserDetails?.avatarUrl || $[4] !== dataContext || $[5] !== isMimirOpen || $[6] !== messages || $[7] !== submitQuestionToAgent) {
    askMimir = t3 => {
      const {
        userFacingMessage,
        questionPrompt,
        clearHistory,
        options,
        customDataContext,
        openIfClosed: t4
      } = t3;
      const openIfClosed = t4 === undefined ? true : t4;
      if (openIfClosed && !isMimirOpen) {
        toggleMimir();
      }
      let conversationContext = [];
      if (clearHistory === true) {
        clearMessageHistory();
      } else {
        conversationContext = getContextFromMessages(messages);
      }
      if (customDataContext != null && dataContext !== customDataContext) {
        setDataContext(customDataContext);
      }
      setMessages(prevMessages => [...prevMessages, chatMessageFactory({
        role: ChatMessageRole.user,
        text: userFacingMessage,
        avatarUrl: currentUserDetails?.avatarUrl
      })]);
      const dataContextToUse = customDataContext ?? dataContext;
      const hookContextPrefix = dataContextToUse != null ? `You are looking at a page on a website. The following data represents what the user is seeing. If relevant use it to answer the user's question: DATA: ${dataContextToUse}\n` : "";
      submitQuestionToAgent({
        question: questionPrompt ?? `Answer the following message as short and concise as possible: \n${userFacingMessage}`,
        skipRag: dataContextToUse != null,
        agentId: MIMIR_BASE_AGENT_ID,
        clientId: currentClient.id,
        contextPrompts: [...conversationContext, ...(dataContextToUse != null ? [{
          content: hookContextPrefix,
          role: ChatMessageRole.user
        }] : [])]
      }, {
        onSuccess: data => {
          const message = chatMessageFactory({
            role: ChatMessageRole.assistant,
            text: data.message,
            agentId: MIMIR_BASE_AGENT_ID,
            relatedDocuments: data.relatedLinks
          });
          setMessages(prevMessages_0 => [...prevMessages_0, message]);
        },
        onError: (error, variables, context) => {
          setMessages(_temp2);
          if (options?.onError != null) {
            options.onError(error, variables, context);
          }
        }
      });
    };
    clearMessageHistory = () => {
      setMessages([]);
    };
    $[2] = currentClient;
    $[3] = currentUserDetails?.avatarUrl;
    $[4] = dataContext;
    $[5] = isMimirOpen;
    $[6] = messages;
    $[7] = submitQuestionToAgent;
    $[8] = askMimir;
    $[9] = clearMessageHistory;
  } else {
    askMimir = $[8];
    clearMessageHistory = $[9];
  }
  let t3;
  if ($[10] !== askMimir || $[11] !== clearMessageHistory || $[12] !== dataContext || $[13] !== isMimirOpen || $[14] !== isSubmittingQuestion || $[15] !== messages || $[16] !== suggestedQuestions) {
    t3 = {
      isMimirOpen,
      toggleMimir,
      messages,
      askMimir,
      clearMessageHistory,
      isWaitingForResponse: isSubmittingQuestion,
      dataContext,
      setDataContext,
      suggestedQuestions,
      setSuggestedQuestions
    };
    $[10] = askMimir;
    $[11] = clearMessageHistory;
    $[12] = dataContext;
    $[13] = isMimirOpen;
    $[14] = isSubmittingQuestion;
    $[15] = messages;
    $[16] = suggestedQuestions;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== children || $[19] !== t3) {
    t4 = <MimirContext.Provider value={t3}>{children}</MimirContext.Provider>;
    $[18] = children;
    $[19] = t3;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
};
function _temp(prev) {
  return !prev;
}
function _temp2(prevMessages_1) {
  return [...prevMessages_1].slice(0, -1);
}